import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../../services/productService';
import { customerService } from '../../services/customerService';
import { saleService } from '../../services/saleService';
import toast from 'react-hot-toast';

const SalesForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    customerName: '',
    product: '',
    quantity: 1,
    totalAmount: 0,
    paymentMethod: 'cash',
    warrantyType: 'standard',
    warrantyDuration: '1 year',
  });

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productService.getProducts();
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p._id === productId);
    const totalAmount = product ? product.pricing.salePrice * formData.quantity : 0;
    setFormData({ ...formData, product: productId, totalAmount });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedQuery = formData.customerName.trim().toLowerCase();
    const exactMatch = customers.find(
      customer => customer.name.trim().toLowerCase() === normalizedQuery
    );

    const partialMatches = customers.filter(customer =>
      customer.name.trim().toLowerCase().includes(normalizedQuery)
    );

    if (partialMatches.length > 1 && !exactMatch) {
      toast.error('Multiple customers match that name. Please refine your search.');
      return;
    }

    const selectedProduct = products.find(product => product._id === formData.product);

    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    setLoading(true);
    try {
      const salePayload = {
        customer: exactMatch?._id || (partialMatches.length === 1 ? partialMatches[0]._id : undefined),
        customerName: exactMatch ? undefined : formData.customerName.trim(),
        items: [
          {
            product: selectedProduct._id,
            quantity: formData.quantity,
            salePrice: selectedProduct.pricing.salePrice,
            purchaseCost: selectedProduct.pricing.costPrice,
            discount: 0,
          },
        ],
        discount: 0,
        tax: 0,
        paymentMethod: formData.paymentMethod,
        paidAmount: formData.totalAmount,
        warranty: {
          provided: true,
          duration: formData.warrantyDuration,
        },
        notes: '',
      };

      await saleService.createSale(salePayload);
      toast.success('Sale created successfully');
      navigate('/admin/sales');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create sale');
    } finally {
      setLoading(false);
    }
  };

  const normalizedCustomerQuery = formData.customerName.trim().toLowerCase();
  const matchingCustomers = customers.filter(customer =>
    customer.name.trim().toLowerCase().includes(normalizedCustomerQuery)
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">New Sale</h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Customer Name</label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              className="input-field"
              placeholder="Enter customer name"
              required
            />
            {normalizedCustomerQuery && matchingCustomers.length > 0 && (
              <p className="mt-2 text-sm text-gray-500">
                Matching customers: {matchingCustomers.slice(0, 3).map(customer => customer.name).join(', ')}
                {matchingCustomers.length > 3 ? '...' : ''}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Product</label>
            <select
              value={formData.product}
              onChange={(e) => handleProductChange(e.target.value)}
              className="input-field"
              required
            >
              <option value="">Select Product</option>
              {products.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.name} - RS:{product.pricing.salePrice}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Quantity</label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                const quantity = parseInt(e.target.value);
                const product = products.find(p => p._id === formData.product);
                const totalAmount = product ? product.pricing.salePrice * quantity : 0;
                setFormData({ ...formData, quantity, totalAmount });
              }}
              className="input-field"
              min="1"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Total Amount</label>
            <input
              type="number"
              value={formData.totalAmount}
              className="input-field bg-gray-100"
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Payment Method</label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              className="input-field"
            >
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="easy_paisa">EasyPaisa</option>
              <option value="jazz_cash">JazzCash</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Warranty Type</label>
            <select
              value={formData.warrantyType}
              onChange={(e) => setFormData({ ...formData, warrantyType: e.target.value })}
              className="input-field"
            >
              <option value="standard">Standard</option>
              <option value="extended">Extended</option>
              <option value="premium">Premium</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4">
          <button type="submit" disabled={loading} className="btn-primary flex-1">
            {loading ? 'Creating...' : 'Create Sale'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/sales')}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default SalesForm;
